/* =========================================================
   Tactile button press — pointer-following squish + tilt
   ---------------------------------------------------------
   Progressive enhancement over the CSS-only :active squish in
   custom.css. Adds the three things that make a soft-body button
   feel alive, without canvas or any third-party library:

     1. the squash happens WHERE you pressed, not uniformly
     2. the button tilts toward the press point
     3. it settles with two decreasing bounces instead of one spring

   Operates on the theme's real <button> elements, so add-to-cart,
   quick-add, variant switching and Buy it now all keep working.
   If this script never loads, custom.css still gives the plain
   squish — nothing breaks.
   ========================================================= */
(function () {
  'use strict';

  var SELECTOR = [
    '.button:not(.button--cta):not(.loading):not(.disabled)',
    '.shopify-challenge__button',
    '.shopify-payment-button__button--unbranded'
  ].join(', ');

  var SETTLE_ANIMATION = 'tjc-btn-settle';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var active = null;
  var enabled = false;

  function eligible(el) {
    if (!el || !el.closest) return null;
    var btn = el.closest(SELECTOR);
    if (!btn) return null;
    // closest() can't express :not([disabled]) reliably across browsers,
    // so re-check the disabled states here.
    if (btn.disabled) return null;
    if (btn.getAttribute('aria-disabled') === 'true') return null;
    return btn;
  }

  function press(btn, clientX, clientY) {
    var rect = btn.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var px = 0;
    var py = 0;

    // Keyboard presses have no coordinates — squash from the centre.
    if (clientX !== null && clientY !== null) {
      px = ((clientX - rect.left) / rect.width - 0.5) * 2;   // -1 (left)  .. 1 (right)
      py = ((clientY - rect.top) / rect.height - 0.5) * 2;   // -1 (top)   .. 1 (bottom)
      px = Math.max(-1, Math.min(1, px));
      py = Math.max(-1, Math.min(1, py));
    }

    btn.style.setProperty('--press-x', px.toFixed(3));
    btn.style.setProperty('--press-y', py.toFixed(3));
    // Move the transform origin toward the press point so the squash
    // reads as local rather than whole-button. Clamped to 28%–72%.
    btn.style.setProperty('--press-ox', (50 + px * 22).toFixed(1) + '%');
    btn.style.setProperty('--press-oy', (50 + py * 22).toFixed(1) + '%');

    btn.classList.remove('is-settling');
    btn.classList.add('is-pressing');
    active = btn;
  }

  function release(btn) {
    if (!btn) return;
    btn.classList.remove('is-pressing');
    btn.classList.remove('is-settling');
    // Force a reflow so the settle animation replays on rapid re-clicks.
    void btn.offsetWidth;
    btn.classList.add('is-settling');
    if (active === btn) active = null;
  }

  function onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    var btn = eligible(e.target);
    if (btn) press(btn, e.clientX, e.clientY);
  }

  function onPointerUp() {
    release(active);
  }

  function onPointerOut(e) {
    // Un-squish if the pointer is dragged off the button while held.
    if (!active) return;
    if (!e.relatedTarget || !active.contains(e.relatedTarget)) release(active);
  }

  function isActivationKey(e) {
    return e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter';
  }

  function onKeyDown(e) {
    if (!isActivationKey(e) || e.repeat) return;
    var btn = eligible(e.target);
    if (btn) press(btn, null, null);
  }

  function onKeyUp(e) {
    if (isActivationKey(e)) release(active);
  }

  function onAnimationEnd(e) {
    if (e.animationName === SETTLE_ANIMATION) {
      e.target.classList.remove('is-settling');
    }
  }

  function enable() {
    if (enabled) return;
    enabled = true;
    // Tells custom.css the enhanced effect is live so the plain
    // :active fallback can stand down.
    document.documentElement.classList.add('js-btn-squish');
    // Delegated from document, so buttons injected later by the cart
    // drawer, quick-add or AJAX pagination are covered automatically.
    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    document.addEventListener('pointerup', onPointerUp, { passive: true });
    document.addEventListener('pointercancel', onPointerUp, { passive: true });
    document.addEventListener('pointerout', onPointerOut, { passive: true });
    document.addEventListener('keydown', onKeyDown, { passive: true });
    document.addEventListener('keyup', onKeyUp, { passive: true });
    document.addEventListener('animationend', onAnimationEnd);
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    document.documentElement.classList.remove('js-btn-squish');
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerUp);
    document.removeEventListener('pointerout', onPointerOut);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);
    document.removeEventListener('animationend', onAnimationEnd);
    if (active) {
      active.classList.remove('is-pressing', 'is-settling');
      active = null;
    }
  }

  function sync() {
    if (reduce.matches) disable();
    else enable();
  }

  sync();
  // Respond if the OS motion preference changes mid-session.
  if (reduce.addEventListener) reduce.addEventListener('change', sync);
  else if (reduce.addListener) reduce.addListener(sync);
})();
